import {
  Stack,
  Text,
  Icon,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  FormattedRelativeTime,
  Spinner,
} from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { isEmptyString } from '@/utils/string';
import { getTaskTypeInfo, type BackfillType } from '@/features/tasks/utils/task-types';

export function TasksPanel({ data }: { data: ServicesApiEnvHealthResponse }) {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { tasks, stats } = data;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle text="product-h4" color="subtle">
          <InlineStack gap="050" alignBlock="center">
            <Icon name="setup-guide" size="sm" color="bold" />
            Tasks
          </InlineStack>
        </PanelTitle>
        <Link href={`/workspaces/${workspaceId}/tasks`} variant="secondary">
          <Text size="xs">View all</Text>
        </Link>
      </PanelHeader>
      <PanelSection>
        <InlineStack gap="300">
          {tasks.running != null ? (
            <Text size="s">
              <Text size="s" weight="semibold">
                1
              </Text>{' '}
              running
            </Text>
          ) : null}
          {tasks.queued_count > 0 ? (
            <Text size="s">
              <Text size="s" weight="semibold">
                {tasks.queued_count}
              </Text>{' '}
              queued
            </Text>
          ) : null}
          {stats.tasks_failed_today > 0 ? (
            <Text size="s">
              <Text size="s" weight="semibold">
                {stats.tasks_failed_today}
              </Text>{' '}
              failed
            </Text>
          ) : null}
          {tasks.running == null && tasks.queued_count === 0 && stats.tasks_failed_today === 0 ? (
            <Text size="s" color="subtle">
              No active tasks
            </Text>
          ) : null}
        </InlineStack>
      </PanelSection>
      {tasks.running != null ? (
        <PanelSection>
          <RunningTaskSummary task={tasks.running} />
        </PanelSection>
      ) : null}
      {tasks.failed.length > 0 ? (
        <PanelSection>
          <Stack gap="050">
            <Text size="xs" color="subtle" weight="semibold">
              Recent failure
            </Text>
            <Text size="s" weight="semibold">
              {getTaskTypeInfo(tasks.failed[0].type as BackfillType).label}
              {!isEmptyString(tasks.failed[0].object_name) ? ` — ${tasks.failed[0].object_name}` : null}
            </Text>
            {!isEmptyString(tasks.failed[0].error) ? (
              <Text size="xs" color="subtle">
                {tasks.failed[0].error}
              </Text>
            ) : null}
            {tasks.failed[0].failed_at != null && tasks.failed[0].failed_at > 0 ? (
              <FormattedRelativeTime date={new Date(tasks.failed[0].failed_at * 1000)} size="xs" color="bold" />
            ) : null}
          </Stack>
        </PanelSection>
      ) : null}
    </Panel>
  );
}

function RunningTaskSummary({ task }: { task: ServicesApiEnvHealthResponse['tasks']['running'] }) {
  if (task == null) return null;

  return (
    <Stack gap="050">
      <InlineStack gap="100" alignBlock="center">
        <Spinner color="accent" />
        <Text size="s" weight="semibold">
          {getTaskTypeInfo(task.type as BackfillType).label}
        </Text>
        {!isEmptyString(task.object_name) ? (
          <Text size="xs" color="subtle">
            {task.object_name}
          </Text>
        ) : null}
      </InlineStack>
      {task.started != null && task.started > 0 ? (
        <InlineStack gap="050" alignBlock="center">
          <Text size="xs" color="subtle">
            Started
          </Text>
          <FormattedRelativeTime date={new Date(task.started * 1000)} size="xs" color="bold" />
        </InlineStack>
      ) : null}
    </Stack>
  );
}
